import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ContactDialog from '@/components/ContactDialog';
import { 
  Zap, 
  Mic, 
  UserCheck, 
  FileText, 
  Network, 
  ChevronRight, 
  Bot, 
  Mail, 
  Headphones,
  LayoutDashboard,
  Brain
} from 'lucide-react';
import PageHero from '@/components/ui/page-hero';
import PageSection from '@/components/ui/page-section';
import SectionHeader from '@/components/ui/section-header';
import BetaProgramCTA from '@/components/BetaProgramCTA';

const AIApps: React.FC = () => {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Five Core AI Solutions
  const aiSolutions = [
    {
      icon: <Zap className="h-12 w-12 text-primary" />,
      title: "AI Automation & Business Systems",
      description: "Streamline operations and reduce costs with intelligent automation",
      features: [
        "AI-driven workflows that save time & reduce costs",
        "Custom automation for business operations",
        "AI-powered analytics & decision support"
      ]
    },
    {
      icon: <Mic className="h-12 w-12 text-primary" />,
      title: "AI Voice Apps & Assistants",
      description: "Elevate customer experiences with natural voice interactions",
      features: [
        "AI-powered voice assistants for customer support & sales",
        "Voice AI chatbots integrated with GoHighLevel & CRM tools",
        "Custom voice AI solutions for business communication"
      ]
    },
    {
      icon: <UserCheck className="h-12 w-12 text-primary" />,
      title: "AI Sales, Marketing & Customer Support",
      description: "Supercharge your revenue operations with intelligent automation",
      features: [
        "AI chatbots & automation for lead generation",
        "AI-powered follow-ups & CRM automation",
        "AI-driven customer support chat & email response systems"
      ]
    },
    {
      icon: <FileText className="h-12 w-12 text-primary" />,
      title: "AI Content Development & Automation",
      description: "Create compelling content at scale with AI-powered solutions",
      features: [
        "AI-generated blogs, social media content, and marketing materials",
        "AI-powered email campaigns & landing page creation",
        "Personalized content automation for businesses"
      ]
    },
    {
      icon: <Network className="h-12 w-12 text-primary" />,
      title: "Integration & AI Dashboard Development",
      description: "Connect and visualize your AI-powered business systems",
      features: [
        "AI tools that seamlessly connect with CRM, ERP, and marketing platforms",
        "Custom dashboard creation for AI analytics & business intelligence",
        "Real-time AI insights & automation tracking"
      ]
    },
    {
      icon: <Brain className="h-12 w-12 text-primary" />,
      title: "AI Research & Persona Development",
      description: "Leverage deep insights with advanced AI market research",
      features: [
        "AI-generated audience personas and market insights",
        "Custom research models trained on your industry data",
        "Actionable intelligence for product development and marketing"
      ]
    }
  ];

  // Featured AI Applications
  const featuredApps = [
    {
      icon: <Bot className="h-12 w-12 text-white" />,
      title: "AI Voice Chatbot",
      subtitle: "GoHighLevel Integration",
      description: "AI-powered voice assistant that manages sales calls, bookings & customer service",
      image: "/placeholder.svg",
      bgColor: "bg-gradient-to-br from-blue-600 to-indigo-700"
    },
    {
      icon: <FileText className="h-12 w-12 text-white" />,
      title: "AI Content Generator",
      subtitle: "For Sales & Marketing",
      description: "AI that writes blogs, email campaigns & landing pages tailored to your brand voice",
      image: "/placeholder.svg",
      bgColor: "bg-gradient-to-br from-purple-600 to-pink-600"
    },
    {
      icon: <Mail className="h-12 w-12 text-white" />,
      title: "AI-Driven Lead Generation",
      subtitle: "CRM Automation",
      description: "AI chatbots that engage leads, qualify them, and follow up automatically",
      image: "/placeholder.svg",
      bgColor: "bg-gradient-to-br from-green-600 to-teal-600"
    },
    {
      icon: <Headphones className="h-12 w-12 text-white" />,
      title: "AI-Powered Customer Support",
      subtitle: "24/7 Assistance",
      description: "AI that responds to customer inquiries & handles FAQs with human-like understanding",
      image: "/placeholder.svg",
      bgColor: "bg-gradient-to-br from-orange-500 to-red-600"
    }
  ];

  // Beta program benefits
  const betaBenefits = [
    "Early access to AI apps before public launch",
    "Custom setup & AI integration support",
    "Special pricing & direct collaboration with our team",
    "Influence product development with your feedback"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <PageHero 
        badge="AI Solutions"
        title="AI Apps That Drive Business Growth"
        description="Our AI-powered applications help businesses automate workflows, enhance customer engagement, and drive revenue with cutting-edge AI technology."
        buttons={[
          {
            text: "Get Early Access",
            variant: "default",
            href: "/beta-signup"
          },
          {
            text: "Book a Demo",
            variant: "outline",
            component: (
              <ContactDialog 
                trigger={
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="bg-transparent border-white/20 text-white hover:bg-white/10"
                  >
                    Book a Demo
                  </Button>
                }
                title="Request a Demo"
                description="Complete this form to schedule a demo of our AI solutions."
                buttonText="Request Demo"
                type="demo"
              />
            )
          }
        ]}
      />

      {/* AI Solutions Overview */}
      <PageSection>
        <SectionHeader 
          badge="What We Offer"
          title="Explore our AI-powered solutions across automation, voice, sales, and content."
          badgeClassName="text-white"
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {aiSolutions.map((solution, index) => (
            <Card 
              key={index} 
              className="border border-border bg-card hover:shadow-lg transition-all duration-300 h-full"
            >
              <CardContent className="p-8">
                <div className="mb-6 p-4 rounded-full bg-primary/10 inline-block">
                  {solution.icon}
                </div>
                <h3 className="text-2xl font-bold mb-3">
                  {solution.title}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {solution.description}
                </p>
                
                <ul className="space-y-2 mb-6">
                  {solution.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start">
                      <span className="mr-2 text-primary">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <ContactDialog 
            trigger={
              <Button 
                className="btn-hover-effect group"
                size="lg"
              >
                Talk to an AI Expert
                <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            }
            title="Talk to an AI Expert"
            description="Fill out the form below to schedule a consultation with our AI experts."
            buttonText="Submit Request"
            type="discovery"
          />
        </div>
      </PageSection>

      {/* Featured AI Applications */}
      <PageSection dark id="featured-apps">
        <SectionHeader 
          badge="Featured AI Applications"
          title="Featured AI Applications" 
          description="Discover our most powerful AI applications designed to solve real business challenges."
          descriptionClassName="text-white/80"
          badgeClassName="text-white"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {featuredApps.map((app, index) => (
            <Card 
              key={index} 
              className={`border-none overflow-hidden ${app.bgColor} text-white card-hover`}
            >
              <CardContent className="p-8">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="mb-6 p-4 rounded-full bg-white/10 inline-block">
                      {app.icon}
                    </div>
                    <h3 className="text-2xl font-bold mb-1">
                      {app.title}
                    </h3>
                    <p className="text-white/80 mb-4 text-sm font-medium">
                      {app.subtitle}
                    </p>
                    <p className="text-white/90 mb-6 max-w-md">
                      {app.description}
                    </p>
                  </div>
                  
                  <div className="hidden lg:block">
                    <div className="bg-white/10 p-1 rounded-lg">
                      <img 
                        src={app.image} 
                        alt={app.title} 
                        className="w-32 h-32 object-cover rounded opacity-90"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <ContactDialog 
            trigger={
              <Button 
                className="btn-hover-effect group bg-white text-gray-900 hover:bg-white/90"
                size="lg"
              >
                Book Discovery Call
                <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            }
            title="Book a Discovery Call"
            description="Fill out the form below and our team will contact you to schedule a call."
            buttonText="Submit"
            type="discovery"
          />
        </div>
      </PageSection>

      {/* Beta Testing Section */}
      <PageSection>
        <div className="flex flex-col md:flex-row gap-12 items-center">
          <div className="md:w-1/2">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/20 text-primary mb-4">
              <span className="text-sm font-medium">Beta Program</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Try Our AI Apps Before They Launch</h2>
            <p className="text-lg text-gray-700 mb-8">
              We're rolling out our AI applications to select businesses before full release. Join our beta program and gain early access.
            </p>
            
            <h3 className="text-xl font-bold mb-4">What Beta Testers Get:</h3>
            <ul className="space-y-4 mb-8">
              {betaBenefits.map((benefit, index) => (
                <li key={index} className="flex items-start">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <svg className="h-3.5 w-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-gray-800">{benefit}</span>
                </li>
              ))}
            </ul>
            
            <Button 
              size="lg" 
              className="btn-hover-effect group"
              asChild
            >
              <Link to="/beta-signup">
                Apply for Beta Access
                <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
          
          <div className="md:w-1/2">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-xl border border-blue-100 relative overflow-hidden">
              {/* AI-themed visual effect */}
              <div className="absolute inset-0 opacity-30">
                <div className="absolute top-0 left-0 w-full h-full">
                  {[...Array(8)].map((_, i) => (
                    <div 
                      key={i}
                      className="absolute rounded-full bg-blue-600/30"
                      style={{
                        width: `${Math.random() * 100 + 50}px`,
                        height: `${Math.random() * 100 + 50}px`,
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                        opacity: Math.random() * 0.5 + 0.1,
                        transform: `scale(${Math.random() * 0.5 + 0.5})`,
                      }}
                    />
                  ))}
                </div>
              </div>
              
              <div className="relative z-10">
                <div className="rounded-full bg-blue-100 p-4 inline-flex mb-6">
                  <LayoutDashboard className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold mb-4">Current Beta Applications</h3>
                
                <div className="space-y-4 mb-8">
                  {[
                    {
                      name: "AI Voice Chatbot",
                      status: "Limited Access",
                      description: "Voice AI integrated with GoHighLevel"
                    },
                    {
                      name: "AI Custom Personas",
                      status: "New",
                      description: "Interactive AI-driven experiences"
                    },
                    {
                      name: "Sales AI Assistant",
                      status: "Coming Soon",
                      description: "Automated follow-ups and lead nurturing"
                    }
                  ].map((app, i) => (
                    <div key={i} className="flex items-start border-b border-blue-100 pb-4">
                      <div className="flex-grow">
                        <div className="flex items-center">
                          <h4 className="font-semibold text-gray-900">{app.name}</h4>
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                            {app.status}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm">{app.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                
                <p className="text-gray-700 text-sm">
                  Beta spots are limited and applications are reviewed within 2 business days.
                </p>
              </div>
            </div>
          </div>
        </div>
      </PageSection>
      
      {/* Final CTA Section */}
      <PageSection dark>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Build AI Into Your Business?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Take the first step toward transforming your business with our powerful AI applications.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg" 
              className="btn-hover-effect group"
              asChild
            >
              <Link to="/beta-signup">
                Get Early Access
              </Link>
            </Button>
            <ContactDialog 
              trigger={
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="bg-transparent border-white/20 text-white hover:bg-white/10"
                >
                  Book a Demo
                </Button>
              }
              title="Request a Demo"
              description="Complete this form to schedule a demo of our AI solutions."
              buttonText="Request Demo"
              type="demo"
            />
            <ContactDialog 
              trigger={
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="bg-transparent border-white/20 text-white hover:bg-white/10"
                >
                  Contact Us
                </Button>
              }
              title="Contact Us"
              description="Have questions or need assistance? Send us a message and we'll get back to you soon."
              buttonText="Send Message"
              type="contact"
            />
          </div>
        </div>
      </PageSection>

      <Footer />
    </div>
  );
};

export default AIApps;
